/**
 * What a lesson earns ([Economy](../../docs/ECONOMY.md),
 * [Gamification](../../docs/GAMIFICATION.md)).
 *
 * Two numbers doing two different jobs. **XP** measures how much a child has
 * learned and only ever goes up; **acorns** are what they can spend. Keeping
 * them apart means the record of learning can never be spent away, and buying a
 * hat never makes progress look smaller.
 *
 * Three rules here are deliberate and easy to get backwards:
 *
 * - **A wrong answer still earns XP.** Trying is the behaviour being
 *   reinforced, so the numbers say so.
 * - **Shadow mode earns exactly what the microphone earns.** The mic is
 *   optional throughout ([Accessibility](../../docs/ACCESSIBILITY.md)), and a
 *   child on a bus must not fall behind one at a desk.
 * - **Nothing is ever taken away.** Getting one wrong ends a combo; it does not
 *   remove what the combo already paid.
 *
 * The rates are provisional and live together at the top so tuning is one
 * reviewable diff rather than a hunt.
 */
// --- the rates --------------------------------------------------------------
export const XP_RATES = {
    /** Right, with no help asked for. */
    correct: 10,
    /** Right, after a hint — or right about part of it. Still most of the credit. */
    withHelp: 7,
    /** Tried, and ended up wrong. Never zero: trying is the point. */
    attempted: 3,
    /** Saying it out loud is more work than tapping, whether or not a mic heard it. */
    speaking: 3,
    lessonComplete: 20,
    chapterComplete: 50,
    /** Per day of streak, capped so day 400 is not worth 2000. */
    perStreakDay: 5,
    streakDayCap: 10,
    /** The first day back after a break. Bigger than an ordinary day, on purpose. */
    comeback: 30,
};
export const ACORN_RATES = {
    lessonComplete: 10,
    /** No wrong answers at all. Nearly double, because it is genuinely harder. */
    lessonPerfect: 18,
    /** Each time a combo reaches another multiple of five. */
    comboOfFive: 5,
    /** A correct answer while hard mode is on. */
    hardModeCorrect: 2,
};
/**
 * Hard mode multiplies what an answer is worth.
 *
 * Generous on purpose: hard mode is optional difficulty a child opted into by
 * being right five times, and it should feel like it paid off.
 */
export const HARD_MODE = { xp: 1.5, acorns: 2 };
/** Consecutive correct answers before hard mode switches on. */
export const HARD_MODE_AT = 5;
export const NO_COMBO = { run: 0, best: 0, hard: false, fives: 0 };
/**
 * Move the combo on by one answer.
 *
 * Only a **wrong** answer breaks it. A partial and a skip leave it exactly where
 * it was rather than resetting it: the design says *getting one wrong* ends hard
 * mode, and a half-right matching puzzle is not getting one wrong. Neither does
 * either of them extend it — the run counts correct answers.
 */
export function afterAnswer(combo, outcome) {
    if (outcome === 'incorrect') {
        // Nothing is taken away: `best` and `fives` survive, so the acorns the run
        // already earned are already earned.
        return { ...combo, run: 0, hard: false };
    }
    if (outcome !== 'correct')
        return combo;
    const run = combo.run + 1;
    return {
        run,
        best: Math.max(combo.best, run),
        hard: combo.hard || run >= HARD_MODE_AT,
        fives: combo.fives + (run % HARD_MODE_AT === 0 ? 1 : 0),
    };
}
/** The XP an answer is worth on its own, before hard mode. */
export function baseXp(answer) {
    const earned = answer.outcome === 'correct'
        ? answer.hintsUsed > 0
            ? XP_RATES.withHelp
            : XP_RATES.correct
        : answer.outcome === 'partial'
            ? XP_RATES.withHelp
            : answer.outcome === 'incorrect'
                ? XP_RATES.attempted
                : // Skipped. Not an attempt, so not paid — the same reasoning that has
                    // a skip leave the FSRS schedule alone ([Review](../../docs/REVIEW.md)).
                    0;
    return earned === 0 ? 0 : earned + (answer.speaking ? XP_RATES.speaking : 0);
}
/**
 * What one answer earns, given the combo it was answered under.
 *
 * `hard` is read from the combo *before* this answer, because that is the mode
 * the child was actually playing in when they gave it.
 */
export function earnAnswer(answer, combo) {
    const base = baseXp(answer);
    const xp = combo.hard ? Math.round(base * HARD_MODE.xp) : base;
    const acorns = combo.hard && answer.outcome === 'correct'
        ? ACORN_RATES.hardModeCorrect * HARD_MODE.acorns
        : 0;
    return { xp, acorns };
}
/**
 * Score a finished lesson.
 *
 * Order matters: the combo is built up answer by answer, so an answer is paid
 * at the rate that was in force when it was given. Handing this the answers in
 * a different order would pay a different number, which is why the server sorts
 * them before it calls.
 *
 * A lesson with nothing in it earns nothing — not the completion bonus either.
 * There is no achievement in opening a screen.
 */
export function earnLesson(answers) {
    if (answers.length === 0) {
        return { xp: 0, acorns: 0, bestCombo: 0, hardMode: false, perfect: false };
    }
    let combo = NO_COMBO;
    let xp = 0;
    let acorns = 0;
    for (const answer of answers) {
        const earnedNow = earnAnswer(answer, combo);
        xp += earnedNow.xp;
        acorns += earnedNow.acorns;
        combo = afterAnswer(combo, answer.outcome);
    }
    const perfect = !answers.some((a) => a.outcome === 'incorrect');
    return {
        xp: xp + XP_RATES.lessonComplete,
        acorns: acorns +
            combo.fives * ACORN_RATES.comboOfFive +
            (perfect ? ACORN_RATES.lessonPerfect : ACORN_RATES.lessonComplete),
        bestCombo: combo.best,
        hardMode: combo.best >= HARD_MODE_AT,
        perfect,
    };
}
//# sourceMappingURL=economy.js.map